import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Send, Trash2 } from 'lucide-react';
import {
  REQUEST_TYPES,
  SOCIAL_TYPES,
  type RequestType,
  type SocialType,
  type TranslatableString,
} from '@geoic/shared';
import { api } from '@/lib/api-client.js';
import { queryKeys } from '@/lib/query-keys.js';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader, PageHeader } from '@/components/ui/Card.js';
import { Checkbox, Input, Select } from '@/components/ui/Field.js';
import { Tabs } from '@/components/ui/Tabs.js';
import { useToast } from '@/components/ui/Toast.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';

type TelegramRoute = { requestType: string; chatId: string; threadId: string | null };

type Social = { type: SocialType; url: string; isVisible: boolean };

type ContactEntry = { label: string; value: string };

type Contacts = {
  phones: ContactEntry[];
  emails: ContactEntry[];
  address: TranslatableString;
  workingHours: TranslatableString;
};

const EMPTY_CONTACTS: Contacts = {
  phones: [],
  emails: [],
  address: { uz: '', ru: '', en: '' },
  workingHours: { uz: '', ru: '', en: '' },
};

const SOCIAL_OPTIONS = SOCIAL_TYPES.map((type) => ({
  value: type,
  label: type.charAt(0).toUpperCase() + type.slice(1),
}));

type SettingsView = {
  siteName?: TranslatableString;
  contacts?: Partial<Contacts>;
  socials?: Social[];
  telegram?: {
    isEnabled?: boolean;
    /** Token HECH QACHON qaytmaydi — faqat «to'ldirilgan» belgisi. */
    botTokenIsSet?: boolean;
    routes?: TelegramRoute[];
  };
};

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  product: 'Mahsulot',
  cart: 'Ariza ro‘yxati',
  drone_show: 'Dron-shou',
  repair: 'Ta’mirlash',
  course: 'Kurs',
  service: 'Xizmat',
  general: 'Umumiy',
};

const TYPE_OPTIONS = REQUEST_TYPES.map((type) => ({
  value: type,
  label: REQUEST_TYPE_LABELS[type],
}));

/**
 * Telefon yoki pochta qatorlari.
 *
 * YORLIQ («Devonxona», «Savdo bo'limi») ixtiyoriy: u qiymatning
 * yonida, kimga tegishli ekanini bildiradi. Sxemada yorliq oddiy
 * satr — tarjima qilinmaydi, shuning uchun uchala tilda bir xil
 * ko'rinadi (kirillchada avtomatik o'giriladi).
 */
function ContactRows({
  items,
  placeholder,
  kind,
  onChange,
}: {
  items: ContactEntry[];
  placeholder: string;
  kind: string;
  onChange: (items: ContactEntry[]) => void;
}) {
  const update = (index: number, patch: Partial<ContactEntry>) =>
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  return (
    <>
      {items.length === 0 && <p className="text-sm text-n-600">Qo‘shilmagan.</p>}

      {items.map((item, index) => (
        <div key={index} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <Input
            label={index === 0 ? kind : undefined}
            aria-label={kind}
            value={item.value}
            placeholder={placeholder}
            onChange={(event) => update(index, { value: event.target.value })}
          />
          <Input
            label={index === 0 ? 'Yorliq (ixtiyoriy)' : undefined}
            aria-label={`${kind} yorlig‘i`}
            value={item.label}
            placeholder="Devonxona"
            onChange={(event) => update(index, { label: event.target.value })}
          />
          <Button
            variant="ghost"
            aria-label={`${kind}ni o‘chirish`}
            onClick={() => onChange(items.filter((_, i) => i !== index))}
          >
            <Trash2 size={16} />
          </Button>
        </div>
      ))}

      <Button variant="secondary" onClick={() => onChange([...items, { label: '', value: '' }])}>
        <Plus size={16} /> Qo‘shish
      </Button>
    </>
  );
}

/**
 * Sozlamalar (B6 DoD: Telegram yo'naltirish + test xabar).
 *
 * Bot tokeni sahifada HECH QACHON ko'rsatilmaydi — server uni shifrlangan
 * holda saqlaydi va javobga qo'shmaydi (TZ 11.3). Maydon bo'sh qoldirilsa
 * eski token o'zgarmaydi.
 */
export function SettingsPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => api.get<{ data: SettingsView }>('/settings'),
  });

  const [siteName, setSiteName] = useState<TranslatableString>({ uz: '', ru: '', en: '' });
  const [enabled, setEnabled] = useState(false);
  const [botToken, setBotToken] = useState('');
  const [routes, setRoutes] = useState<TelegramRoute[]>([]);
  const [socials, setSocials] = useState<Social[]>([]);
  const [contacts, setContacts] = useState<Contacts>(EMPTY_CONTACTS);
  const [tab, setTab] = useState<'telegram' | 'general' | 'contacts' | 'socials'>('telegram');

  useEffect(() => {
    if (!data) return;
    setSiteName(data.data.siteName ?? { uz: '', ru: '', en: '' });
    setEnabled(data.data.telegram?.isEnabled ?? false);
    setRoutes(data.data.telegram?.routes ?? []);
    setSocials(data.data.socials ?? []);
    setContacts({ ...EMPTY_CONTACTS, ...(data.data.contacts ?? {}) });
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/settings', {
        siteName,
        // Qiymatsiz qator saqlanmaydi — saytda bo'sh havola bo'lib
        // qolardi
        contacts: {
          ...contacts,
          phones: contacts.phones.filter((entry) => entry.value.trim() !== ''),
          emails: contacts.emails.filter((entry) => entry.value.trim() !== ''),
        },
        socials: socials.filter((social) => social.url.trim() !== ''),
        telegram: {
          isEnabled: enabled,
          // Bo'sh bo'lsa server eski tokenni saqlab qoladi
          botToken,
          routes,
        },
      }),
    onSuccess: async () => {
      setBotToken('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.settings });
      toast.success('Sozlamalar saqlandi');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  /**
   * Test xabar HAQIQATAN Telegram'ga yuboriladi (B6 DoD).
   *
   * Shuning uchun avval sozlamalar saqlanadi: aks holda foydalanuvchi
   * hozirgina yozgan token bilan emas, eskisi bilan sinalardi.
   */
  const sendTest = useMutation({
    mutationFn: async (requestType: string) => {
      await save.mutateAsync();
      return api.post<{ data: { ok: boolean; error: string | null } }>('/settings/telegram/test', {
        requestType,
      });
    },
    onSuccess: (result) => {
      if (result.data.ok) toast.success('Sinov xabari yuborildi — guruhni tekshiring');
      else toast.error(result.data.error ?? 'Xabar yuborilmadi');
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (isLoading) return <p className="text-sm text-n-600">Yuklanmoqda…</p>;

  const botTokenIsSet = data?.data.telegram?.botTokenIsSet ?? false;

  return (
    <>
      <PageHeader
        title="Sozlamalar"
        description="Sayt nomi va arizalarni Telegram‘ga yo‘naltirish"
        action={
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Saqlash
          </Button>
        }
      />

      {/* Maketda sozlamalar YORLIQLARGA bo'lingan (`D5`, SOZLAMALAR) */}
      <Tabs
        items={[
          { key: 'telegram', label: 'Telegram' },
          { key: 'contacts', label: 'Kontaktlar' },
          { key: 'socials', label: 'Ijtimoiy tarmoqlar' },
          { key: 'general', label: 'Umumiy' },
        ]}
        value={tab}
        onChange={setTab}
        className="mb-4"
      />

      {tab === 'general' && (
        <div className="max-w-3xl">
          <Card>
            <CardHeader title="Umumiy" />
            <CardBody>
              <TranslatableInput
                label="Sayt nomi"
                value={siteName}
                onChange={setSiteName}
                required
              />
            </CardBody>
          </Card>
        </div>
      )}

      {tab === 'contacts' && (
        <div className="max-w-3xl space-y-4">
          <Card>
            <CardHeader
              title="Telefonlar"
              description="Birinchi raqam sarlavhaning yuqori chizig‘ida ko‘rinadi"
            />
            <CardBody className="space-y-3">
              <ContactRows
                items={contacts.phones}
                placeholder="+998 71 276 10 76"
                onChange={(phones) => setContacts((current) => ({ ...current, phones }))}
                kind="Telefon"
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Elektron pochta" />
            <CardBody className="space-y-3">
              <ContactRows
                items={contacts.emails}
                placeholder="info@geoic.uz"
                onChange={(emails) => setContacts((current) => ({ ...current, emails }))}
                kind="Pochta"
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Manzil va ish vaqti" />
            <CardBody className="space-y-4">
              <TranslatableInput
                label="Manzil"
                multiline
                rows={2}
                value={contacts.address}
                onChange={(address) => setContacts((current) => ({ ...current, address }))}
              />
              <TranslatableInput
                label="Ish vaqti"
                value={contacts.workingHours}
                onChange={(workingHours) =>
                  setContacts((current) => ({ ...current, workingHours }))
                }
              />
            </CardBody>
          </Card>
        </div>
      )}

      {tab === 'socials' && (
        <div className="max-w-3xl">
          <Card>
            <CardHeader
              title="Ijtimoiy tarmoqlar"
              description="Havolalar sarlavhaning yuqori chizig‘ida va footerda ko‘rinadi"
            />
            <CardBody className="space-y-3">
              {socials.length === 0 && <p className="text-sm text-n-600">Havola qo‘shilmagan.</p>}

              {socials.map((social, index) => (
                <div key={index} className="grid gap-3 sm:grid-cols-[170px_1fr_auto] sm:items-end">
                  <Select
                    label={index === 0 ? 'Tarmoq' : undefined}
                    aria-label="Tarmoq"
                    options={SOCIAL_OPTIONS}
                    value={social.type}
                    onChange={(event) =>
                      setSocials((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, type: event.target.value as SocialType } : item,
                        ),
                      )
                    }
                  />
                  <Input
                    label={index === 0 ? 'Havola' : undefined}
                    aria-label="Havola"
                    value={social.url}
                    placeholder="https://t.me/geoic"
                    onChange={(event) =>
                      setSocials((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, url: event.target.value } : item,
                        ),
                      )
                    }
                  />
                  <div className="flex items-center gap-2">
                    <Checkbox
                      label="Ko‘rinsin"
                      checked={social.isVisible}
                      onChange={(event) =>
                        setSocials((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, isVisible: event.target.checked } : item,
                          ),
                        )
                      }
                    />
                    <Button
                      variant="ghost"
                      aria-label="Havolani o‘chirish"
                      onClick={() => setSocials((current) => current.filter((_, i) => i !== index))}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}

              <Button
                variant="secondary"
                onClick={() =>
                  setSocials((current) => [
                    ...current,
                    { type: 'telegram', url: '', isVisible: true },
                  ])
                }
              >
                <Plus size={16} /> Havola qo‘shish
              </Button>
            </CardBody>
          </Card>
        </div>
      )}

      {tab === 'telegram' && (
        <div className="flex flex-col gap-4">
          <Card className="grid gap-5 p-5 lg:grid-cols-[1fr_260px] lg:items-end">
            <Input
              label="Bot tokeni"
              type="password"
              className="font-mono"
              value={botToken}
              onChange={(event) => setBotToken(event.target.value)}
              placeholder={botTokenIsSet ? '•••••••• (saqlangan)' : '7123456789:AA...'}
              hint={
                botTokenIsSet
                  ? 'Token saqlangan va ko‘rsatilmaydi. Almashtirish uchun yangisini kiriting.'
                  : '@BotFather dan olingan token. Bazada shifrlangan holda saqlanadi.'
              }
              autoComplete="off"
            />
            <Checkbox
              label="Yuborish yoqilgan"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
            />
          </Card>

          {/*
            Yo'naltirish jadvali maketda QORA chegarali panelda —
            bu bo'limning asosiy qismi. «Test xabar yuborish» esa
            panel sarlavhasida, jadvalning yonida.
          */}
          <section className="border border-n-900 bg-white">
            <div className="flex flex-wrap items-center justify-between gap-4 border-b border-n-200 px-5 py-4">
              <div>
                <p className="text-[15px] font-semibold text-n-900">Ariza turlarini yo‘naltirish</p>
                <p className="mt-0.5 text-[12.5px] text-n-600">
                  Har bir ariza turi o‘z guruhiga tushadi
                </p>
              </div>
              <Button
                variant="outline"
                icon={<Send className="size-4" aria-hidden />}
                loading={sendTest.isPending}
                onClick={() => sendTest.mutate('general')}
                className="border-brand-600 text-brand-600 hover:bg-brand-50"
              >
                Test xabar yuborish
              </Button>
            </div>

            <RoutesEditor routes={routes} onChange={setRoutes} />
          </section>
        </div>
      )}
    </>
  );
}

/**
 * Ariza turi → Telegram guruhi JADVALI (TZ 5.4, maket `D5`).
 *
 * `general` zaxira sifatida ishlaydi: turi uchun alohida guruh
 * ko'rsatilmagan arizalar shunga tushadi.
 */
function RoutesEditor({
  routes,
  onChange,
}: {
  routes: TelegramRoute[];
  onChange: (routes: TelegramRoute[]) => void;
}) {
  const update = (index: number, patch: Partial<TelegramRoute>) => {
    onChange(routes.map((route, i) => (i === index ? { ...route, ...patch } : route)));
  };

  return (
    <div>
      {routes.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-n-600">
          Yo‘naltirish qo‘shilmagan. Kamida «Umumiy» turi uchun guruh ko‘rsating — qolgan turlar ham
          shunga tushadi.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13.5px]">
            <thead>
              <tr className="border-b border-n-200 bg-n-50 text-left">
                <th className="px-5 py-3 font-semibold">Ariza turi</th>
                <th className="px-3 py-3 font-semibold">Guruh ID</th>
                <th className="px-3 py-3 font-semibold">Mavzu ID</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {routes.map((route, index) => (
                <tr key={index} className="border-b border-n-100 last:border-b-0">
                  <td className="px-5 py-2">
                    <Select
                      aria-label="Ariza turi"
                      options={TYPE_OPTIONS}
                      value={route.requestType}
                      onChange={(event) => update(index, { requestType: event.target.value })}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      aria-label="Guruh ID"
                      className="font-mono tabular"
                      value={route.chatId}
                      onChange={(event) => update(index, { chatId: event.target.value })}
                      placeholder="-1001234567890"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      aria-label="Mavzu ID"
                      className="font-mono"
                      value={route.threadId ?? ''}
                      onChange={(event) => update(index, { threadId: event.target.value || null })}
                      placeholder="ixtiyoriy"
                    />
                  </td>
                  <td className="px-5 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label="Yo‘naltirishni o‘chirish"
                      onClick={() => onChange(routes.filter((_, i) => i !== index))}
                    >
                      <Trash2 className="size-4 text-error-600" aria-hidden />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-n-100 px-5 py-3.5">
        <Button
          variant="outline"
          size="sm"
          icon={<Plus className="size-4" aria-hidden />}
          onClick={() =>
            onChange([...routes, { requestType: 'general', chatId: '', threadId: null }])
          }
        >
          Yo‘naltirish qo‘shish
        </Button>
      </div>
    </div>
  );
}
